class CartDrawer extends HTMLElement {
  constructor() {
    super();
    
    // Set up ARIA attributes for the modal
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    this.setAttribute('aria-labelledby', 'cart-drawer-title');
    this.setAttribute('aria-describedby', 'cart-drawer-description');
    
    // Initialize focus trap state
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
    this.previouslyFocusedElement = null;
    
    this.addEventListener('keydown', this.handleKeyDown.bind(this));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    
    // Create live region for announcements
    this.createLiveRegion();
  }

  createLiveRegion() {
    if (!document.getElementById('cart-drawer-live-region')) {
      const liveRegion = document.createElement('div');
      liveRegion.id = 'cart-drawer-live-region';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.cssText = 'position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden;';
      document.body.appendChild(liveRegion);
    }
  }

  announceToScreenReader(message) {
    const liveRegion = document.getElementById('cart-drawer-live-region');
    if (liveRegion) {
      liveRegion.textContent = message;
      // Clear after announcement
      setTimeout(() => {
        liveRegion.textContent = '';
      }, 1000);
    }
  }

  handleKeyDown(event) {
    // Handle Escape key
    if (event.code === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    // Handle Tab key for focus trapping
    if (event.key === 'Tab') {
      this.trapTabKey(event);
    }
  }

  trapTabKey(event) {
    if (!this.firstFocusableElement || !this.lastFocusableElement) return;

    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === this.firstFocusableElement) {
        event.preventDefault();
        this.lastFocusableElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === this.lastFocusableElement) {
        event.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  }

  getFocusableElements() {
    const focusableSelectors = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      'details summary'
    ].join(', ');

    this.focusableElements = Array.from(this.querySelectorAll(focusableSelectors))
      .filter(element => {
        return element.offsetWidth > 0 || element.offsetHeight > 0 || element === document.activeElement;
      });

    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-expanded', 'false');
    cartLink.setAttribute('aria-label', 'Open cart drawer');
    
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    
    cartLink.addEventListener('keydown', (event) => {
      if (event.code === 'Space' || event.code === 'Enter') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // Store the previously focused element
    this.previouslyFocusedElement = triggeredBy || document.activeElement;
    
    // Update cart icon aria-expanded
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.setAttribute('aria-expanded', 'true');
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    // Show the drawer
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener('transitionend', () => {
      this.setupFocusTrap();
      this.announceDrawerOpened();
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  setupFocusTrap() {
    // Get all focusable elements
    this.getFocusableElements();
    
    // Focus on the first focusable element or close button
    const closeButton = this.querySelector('.drawer__close');
    const focusTarget = closeButton || this.firstFocusableElement;
    
    if (focusTarget) {
      focusTarget.focus();
    }
  }

  announceDrawerOpened() {
    const itemCount = this.getCartItemCount();
    let message;
    
    if (itemCount === 0) {
      message = 'Cart drawer opened. Your cart is empty.';
    } else if (itemCount === 1) {
      message = 'Cart drawer opened. You have 1 item in your cart.';
    } else {
      message = `Cart drawer opened. You have ${itemCount} items in your cart.`;
    }
    
    this.announceToScreenReader(message);
  }

  getCartItemCount() {
    const cartItems = this.querySelectorAll('[data-cart-item]');
    return cartItems.length;
  }

  close() {
    this.classList.remove('active');
    
    // Update cart icon aria-expanded
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.setAttribute('aria-expanded', 'false');
    }

    // Return focus to the previously focused element
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
      this.previouslyFocusedElement = null;
    }

    document.body.classList.remove('overflow-hidden');
    
    // Announce closure
    this.announceToScreenReader('Cart drawer closed.');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    
    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }
    
    cartDrawerNote.addEventListener('click', (event) => {
      const isExpanded = event.currentTarget.closest('details').hasAttribute('open');
      event.currentTarget.setAttribute('aria-expanded', isExpanded.toString());
    });
    
    cartDrawerNote.parentElement.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') {
        const details = event.currentTarget;
        const summary = details.querySelector('summary');
        if (details.hasAttribute('open')) {
          details.removeAttribute('open');
          summary.setAttribute('aria-expanded', 'false');
          summary.focus();
        }
      }
    });
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && 
    this.querySelector('.drawer__inner').classList.remove('is-empty');
    
    this.productId = parsedState.id;
    
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? 
        document.querySelector(section.selector) : 
        document.getElementById(section.id);
      
      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      
      // Announce item added to cart
      const itemCount = this.getCartItemCount();
      this.announceToScreenReader(`Item added to cart. You now have ${itemCount} item${itemCount !== 1 ? 's' : ''} in your cart.`);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);